import { useState, useEffect } from 'react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAuth } from '@/contexts/AuthContext';
import { githubApi, GitHubConnection, GitHubOrg, GitHubRepo } from '@/lib/api/github';
import toast from 'react-hot-toast';
import { Github, Loader2, Plus, Search, Lock, Unlock, Building, User, AlertTriangle } from 'lucide-react';

interface GitHubImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (projectId: string) => void;
}

type Step = 'select-connection' | 'select-repo' | 'configure';

export default function GitHubImportModal({
  isOpen,
  onClose,
  onSuccess,
}: GitHubImportModalProps) {
  const { getIdToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<Step>('select-connection');

  // Connection state
  const [connections, setConnections] = useState<GitHubConnection[]>([]);
  const [selectedConnectionId, setSelectedConnectionId] = useState<string>('');
  const [selectedConnection, setSelectedConnection] = useState<GitHubConnection | null>(null);

  // Repo selection state
  const [organizations, setOrganizations] = useState<GitHubOrg[]>([]);
  const [selectedOwner, setSelectedOwner] = useState<string>('');
  const [repositories, setRepositories] = useState<GitHubRepo[]>([]);
  const [selectedRepo, setSelectedRepo] = useState<GitHubRepo | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loadingRepos, setLoadingRepos] = useState(false);

  // Config state
  const [branch, setBranch] = useState('main');
  const [branches, setBranches] = useState<string[]>([]);
  const [projectName, setProjectName] = useState('');

  // Load connections on mount
  useEffect(() => {
    if (isOpen) {
      setStep('select-connection');
      loadConnections();
    }
  }, [isOpen]);

  // Load orgs when connection changes
  useEffect(() => {
    if (selectedConnectionId) {
      const conn = connections.find((c) => c.id === selectedConnectionId);
      setSelectedConnection(conn || null);
      if (conn) {
        loadOrganizations(selectedConnectionId);
        setSelectedOwner(conn.metadata?.login || '');
      }
    }
  }, [selectedConnectionId, connections]);

  // Load repos when owner changes
  useEffect(() => {
    if (selectedConnectionId && selectedOwner) {
      loadRepositories();
    }
  }, [selectedConnectionId, selectedOwner]);

  const loadConnections = async () => {
    try {
      const token = await getIdToken();
      if (!token) return;
      const data = await githubApi.listConnections(token);
      setConnections(data);
      if (data.length > 0 && !selectedConnectionId) {
        setSelectedConnectionId(data[0].id);
      }
    } catch (error) {
      console.error('Failed to load GitHub connections', error);
    }
  };

  const loadOrganizations = async (connectionId: string) => {
    try {
      const token = await getIdToken();
      if (!token) return;
      const orgs = await githubApi.listOrganizations(token, connectionId);
      setOrganizations(orgs);
    } catch (error) {
      console.error('Failed to load organizations', error);
    }
  };

  const loadRepositories = async () => {
    setLoadingRepos(true);
    try {
      const token = await getIdToken();
      if (!token) return;
      const repos = await githubApi.listRepositories(token, selectedConnectionId, selectedOwner);
      setRepositories(repos);
    } catch (error) {
      console.error('Failed to load repositories', error);
    } finally {
      setLoadingRepos(false);
    }
  };

  const loadBranches = async (owner: string, repo: string) => {
    try {
      const token = await getIdToken();
      if (!token) return;
      const branchList = await githubApi.listBranches(token, selectedConnectionId, owner, repo);
      setBranches(branchList.map((b: { name: string }) => b.name));
    } catch (error) {
      console.error('Failed to load branches', error);
    }
  };

  const handleConnectGitHub = async () => {
    try {
      setLoading(true);
      const token = await getIdToken();
      if (!token) {
        toast.error('Please sign in to continue');
        return;
      }
      // Pass returnPath to reopen import modal after OAuth completes
      const returnPath = '/dashboard/editor?openImport=true';
      const { authUrl } = await githubApi.initiateOAuth(token, undefined, undefined, returnPath);
      window.location.href = authUrl;
    } catch (error) {
      console.error('Failed to initiate GitHub OAuth', error);
      toast.error('Failed to connect GitHub');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectRepo = async (repo: GitHubRepo) => {
    setSelectedRepo(repo);
    setProjectName(repo.name);
    setBranch(repo.default_branch);
    await loadBranches(repo.owner.login, repo.name);
    setStep('configure');
  };

  const handleImport = async () => {
    if (!selectedRepo || !selectedConnectionId) {
      toast.error('Please select a repository');
      return;
    }

    try {
      setLoading(true);
      const token = await getIdToken();
      if (!token) {
        toast.error('Please sign in to continue');
        return;
      }
      const project = await githubApi.importRepo(token, {
        connectionId: selectedConnectionId,
        owner: selectedRepo.owner.login,
        repoName: selectedRepo.name,
        branch,
        projectName: projectName || selectedRepo.name,
      });
      toast.success('Repository imported successfully!');
      onSuccess(project.id);
      onClose();
    } catch (error: any) {
      console.error('Failed to import repository', error);
      toast.error(error.response?.data?.message || 'Failed to import repository');
    } finally {
      setLoading(false);
    }
  };

  const filteredRepos = searchQuery
    ? repositories.filter(
        (r) =>
          r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          r.description?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : repositories;

  const renderSelectConnection = () => (
    <div className="space-y-4">
      {connections.length === 0 ? (
        <div className="text-center py-8">
          <Github className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground mb-4">No GitHub accounts connected</p>
          <Button onClick={handleConnectGitHub} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Github className="h-4 w-4 mr-2" />}
            Connect GitHub Account
          </Button>
        </div>
      ) : (
        <>
          <div className="space-y-2">
            <Label>GitHub Account</Label>
            <Select value={selectedConnectionId} onValueChange={setSelectedConnectionId}>
              <SelectTrigger>
                <SelectValue placeholder="Select GitHub account" />
              </SelectTrigger>
              <SelectContent>
                {connections.map((conn) => (
                  <SelectItem key={conn.id} value={conn.id}>
                    <div className="flex items-center gap-2">
                      <Avatar className="h-5 w-5">
                        <AvatarImage src={conn.metadata?.avatar_url} />
                        <AvatarFallback>{conn.metadata?.login?.[0]?.toUpperCase()}</AvatarFallback>
                      </Avatar>
                      <span>{conn.metadata?.login || conn.name}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Button variant="outline" size="sm" onClick={handleConnectGitHub} disabled={loading}>
            <Plus className="h-4 w-4 mr-2" />
            Add another account
          </Button>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={() => setStep('select-repo')} disabled={!selectedConnectionId}>
              Next
            </Button>
          </div>
        </>
      )}
    </div>
  );

  const renderSelectRepo = () => (
    <div className="space-y-4">
      {/* Owner selection */}
      <div className="space-y-2">
        <Label>Repository Owner</Label>
        <Select value={selectedOwner} onValueChange={setSelectedOwner}>
          <SelectTrigger>
            <SelectValue placeholder="Select owner" />
          </SelectTrigger>
          <SelectContent>
            {selectedConnection?.metadata?.login && (
              <SelectItem value={selectedConnection.metadata.login}>
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  <span>{selectedConnection.metadata.login}</span>
                  <span className="text-muted-foreground text-xs">(Personal)</span>
                </div>
              </SelectItem>
            )}
            {organizations.map((org) => (
              <SelectItem key={org.id} value={org.login}>
                <div className="flex items-center gap-2">
                  <Avatar className="h-4 w-4">
                    <AvatarImage src={org.avatar_url} />
                    <AvatarFallback><Building className="h-3 w-3" /></AvatarFallback>
                  </Avatar>
                  <span>{org.login}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search repositories..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Repository list */}
      <div className="border rounded-lg max-h-[300px] overflow-y-auto">
        {loadingRepos ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : filteredRepos.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            {searchQuery ? 'No repositories match your search' : 'No repositories found'}
          </div>
        ) : (
          <div className="divide-y">
            {filteredRepos.map((repo) => (
              <button
                key={repo.id}
                onClick={() => handleSelectRepo(repo)}
                className="w-full px-4 py-3 text-left hover:bg-muted transition-colors"
              >
                <div className="flex items-center gap-2">
                  {repo.private ? (
                    <Lock className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Unlock className="h-4 w-4 text-muted-foreground" />
                  )}
                  <span className="font-medium">{repo.name}</span>
                </div>
                {repo.description && (
                  <p className="text-sm text-muted-foreground mt-1 line-clamp-1">
                    {repo.description}
                  </p>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-between gap-2 pt-4">
        <Button variant="outline" onClick={() => setStep('select-connection')}>
          Back
        </Button>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );

  const renderConfigure = () => (
    <div className="space-y-4">
      {selectedRepo && (
        <div className="bg-muted p-4 rounded-lg">
          <div className="flex items-center gap-2">
            <Github className="h-4 w-4" />
            <span className="font-medium">{selectedRepo.full_name}</span>
            {selectedRepo.private && <Lock className="h-3 w-3 text-muted-foreground" />}
          </div>
          {selectedRepo.description && (
            <p className="text-sm text-muted-foreground mt-1">{selectedRepo.description}</p>
          )}
        </div>
      )}

      {/* Deployment requirements warning */}
      <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
        <div className="flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
          <div className="text-sm">
            <p className="font-medium text-amber-500 mb-1">Deployment Requirements</p>
            <p className="text-muted-foreground mb-2">
              Projects can be built and deployed on our platform if they follow:
            </p>
            <ul className="text-muted-foreground space-y-1 text-xs list-disc list-inside">
              <li>Vite project with <code className="bg-muted px-1 rounded">npm run build</code> outputting to <code className="bg-muted px-1 rounded">dist/</code></li>
              <li>Dev server on port <code className="bg-muted px-1 rounded">5173</code> with <code className="bg-muted px-1 rounded">strictPort: true</code></li>
              <li><code className="bg-muted px-1 rounded">server.host: true</code> in vite.config</li>
              <li><code className="bg-muted px-1 rounded">X-Frame-Options: ALLOWALL</code> header for preview</li>
              <li>Static SPA (no SSR or server-side rendering)</li>
            </ul>
            <p className="text-muted-foreground mt-2 text-xs">
              Other projects (Next.js, Python, etc.) can still be developed but cannot be deployed.
            </p>
          </div>
        </div>
      </div>

      {/* Project name */}
      <div className="space-y-2">
        <Label>Project Name</Label>
        <Input
          value={projectName}
          onChange={(e) => setProjectName(e.target.value)}
          placeholder="My Project"
        />
      </div>

      {/* Branch */}
      <div className="space-y-2">
        <Label>Branch</Label>
        <Select value={branch} onValueChange={setBranch}>
          <SelectTrigger>
            <SelectValue placeholder="Select branch" />
          </SelectTrigger>
          <SelectContent>
            {branches.length > 0 ? (
              branches.map((b) => (
                <SelectItem key={b} value={b}>
                  {b}
                </SelectItem>
              ))
            ) : (
              <SelectItem value={branch}>{branch}</SelectItem>
            )}
          </SelectContent>
        </Select>
      </div>

      <div className="flex justify-between gap-2 pt-4">
        <Button variant="outline" onClick={() => setStep('select-repo')}>
          Back
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleImport} disabled={loading || !projectName}>
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Import Repository
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg border-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Github className="h-5 w-5" />
            Import from GitHub
          </DialogTitle>
          <DialogDescription>
            {step === 'select-connection' && 'Select a GitHub account to import from'}
            {step === 'select-repo' && 'Choose a repository to import'}
            {step === 'configure' && 'Configure your import settings'}
          </DialogDescription>
        </DialogHeader>

        {step === 'select-connection' && renderSelectConnection()}
        {step === 'select-repo' && renderSelectRepo()}
        {step === 'configure' && renderConfigure()}
      </DialogContent>
    </Dialog>
  );
}
